package com.bikompanion.ui.composables

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.tween
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.scaleIn
import androidx.compose.animation.scaleOut
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.sizeIn
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import androidx.compose.ui.zIndex
import dev.chrisbanes.haze.HazeInput
import dev.chrisbanes.haze.HazeState
import dev.chrisbanes.haze.glass.hazeGlass
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.preview.PreviewLightDarkCompact
import com.bikompanion.ui.preview.PreviewLightDarkMedium

// Above the sheets: settings is the only thing that opens over them.
private const val GlassWindowZIndex = 4f

// Heavier than a sheet's plate, because this is a page of small text rather than a few big numbers
// and it sits over the busiest part of the map.
private const val WindowPlateAlpha = 0.78f

private val FloatingMaxWidth = 480.dp
private val FloatingMaxHeight = 680.dp
private val FloatingShape = RoundedCornerShape(28.dp)
private val FullScreenShape = RoundedCornerShape(0.dp)

/**
 * A glass surface over everything else: the whole window on a phone, or a centred [floating] one
 * with the map still showing around it. Like [GlassBottomSheet] it is plain content rather than a
 * `Dialog`, whose separate surface Haze cannot blur across.
 *
 * [onBack] is the system back gesture and [onDismiss] a click outside. Callers that page inside it
 * step back with the former, and leave the window with the latter.
 */
@Composable
internal fun GlassWindow(
    visible: Boolean,
    hazeState: HazeState,
    floating: Boolean,
    onBack: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit,
) {
    AnimatedVisibility(
        visible = visible,
        enter = fadeIn(tween(durationMillis = 180)) + if (floating) {
            scaleIn(initialScale = 0.94f)
        } else {
            slideInVertically(initialOffsetY = { it / 8 })
        },
        exit = fadeOut(tween(durationMillis = 180)) + if (floating) {
            scaleOut(targetScale = 0.94f)
        } else {
            slideOutVertically(targetOffsetY = { it / 8 })
        },
        modifier = modifier.zIndex(GlassWindowZIndex),
    ) {
        SheetPredictiveBack(
            enabled = visible,
            onProgress = {},
            onCancel = {},
            onDismiss = onBack,
        )
        Box(modifier = Modifier.fillMaxSize()) {
            if (floating) {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() },
                            onClick = onDismiss,
                        ),
                )
            }
            val shape = if (floating) FloatingShape else FullScreenShape
            val glassTint = MaterialTheme.colorScheme.primaryContainer
            val glassStyle = remember(glassTint, shape) { defaultGlassStyle(glassTint, shape) }
            Surface(
                shape = shape,
                color = Color.Transparent,
                contentColor = MaterialTheme.colorScheme.onSurface,
                modifier = Modifier
                    .align(Alignment.Center)
                    .then(
                        if (floating) {
                            Modifier
                                .windowInsetsPadding(safeDrawingInsets())
                                .padding(24.dp)
                                .sizeIn(maxWidth = FloatingMaxWidth, maxHeight = FloatingMaxHeight)
                        } else {
                            Modifier
                        },
                    ).fillMaxSize()
                    .clip(shape)
                    .hazeGlass(
                        input = HazeInput.Sources(hazeState),
                        style = glassStyle,
                    ).background(MaterialTheme.colorScheme.surfaceContainer.copy(alpha = glassPlateAlpha(WindowPlateAlpha))),
            ) {
                Column(
                    modifier = if (floating) Modifier else Modifier.windowInsetsPadding(safeDrawingInsets()),
                    content = content,
                )
            }
        }
    }
}

@PreviewLightDarkCompact
@Composable
private fun GlassWindowFullScreenPreview() {
    GlassWindowPreview(floating = false)
}

@PreviewLightDarkMedium
@Composable
private fun GlassWindowFloatingPreview() {
    GlassWindowPreview(floating = true)
}

@Composable
private fun GlassWindowPreview(floating: Boolean) {
    GlassPreviewSurface { hazeState ->
        GlassWindow(
            visible = true,
            hazeState = hazeState,
            floating = floating,
            onBack = {},
            onDismiss = {},
        ) {
            Text("Window content", modifier = Modifier.padding(16.dp))
        }
    }
}
